import React, { createContext, useContext, useReducer, useEffect } from 'react';

// Initial form state
const initialFormState = {
  // Form data with default values
  formData: {
    // Photo
    photo: null,
    
    // Personal info
    businessName: '',
    firstName: '',
    surname: '',
    
    // Contact info — address is split into structured fields,
    // joined back into a single "address" string at submission
    streetAddress: '',
    suburb: '',
    state: '',
    postcode: '',
    email: '',
    mobile: '',
    
    // Police check
    policeCheck: null,
    policeCheckIssue: '',
    
    // Work dates
    startWork: '',
    dateOfBirth: '',
    
    // WWCC
    hasWWCC: '',
    wwcc: null,
    wwccExpiry: '',
    
    // Visa
    visaType: '',
    visaExpiry: '',
    visaDocument: null,
    
    // Documents
    passportDocument: null,
    
    // Work status
    workStatus: '',
    tfnNumber: '',
    superFundName: '',
    superFundAccountName: '',
    membershipNumber: '',
    abn: '',
    
    // Bank info
    bankAccountName: '',
    bsbNumber: '',
    bankAccountNumber: '',

    
  },
  
  // Validation state
  formErrors: {},
  
  // Sections that have been visited/modified
  visitedSections: [],
  modifiedFields: new Set(),
  
  // Current step information
  currentStep: 0,
  
  // Submission state
  isSubmitting: false,
  submitMessage: null,
  
  // Progress tracking
  completedSteps: []
};

// Form reducer
function formReducer(state, action) {
  switch (action.type) {
  
    case 'SET_FIELD_VALUE':
      return {
        ...state,
        formData: {
          ...state.formData,
          [action.field]: action.value
        },
        modifiedFields: new Set([...state.modifiedFields, action.field])
      };
      
    case 'SET_FILE':
      return {
        ...state,
        formData: {
          ...state.formData,
          [action.field]: action.file
        },
        modifiedFields: new Set([...state.modifiedFields, action.field])
      };
      
    case 'SET_FORM_ERRORS':
      return {
        ...state,
        formErrors: action.errors
      };
      
    case 'SET_CURRENT_STEP':
      return {
        ...state,
        currentStep: action.step,
        visitedSections: [...new Set([...state.visitedSections, action.step])]
      };
      
    case 'MARK_STEP_COMPLETED':
      return {
        ...state,
        completedSteps: [...new Set([...state.completedSteps, action.step])]
      };
      
    case 'SET_SUBMITTING':
      return {
        ...state,
        isSubmitting: action.isSubmitting
      };
      
    case 'SET_SUBMIT_MESSAGE':
      return {
        ...state,
        submitMessage: action.message
      };

    case 'SET_MODIFIED_FIELDS':
        return {
          ...state,
          modifiedFields: action.fields
        };
      
    case 'INITIALIZE_USER_DATA':
      return {
        ...state,
        formData: {
          ...state.formData,
          ...action.userData
        }
      };
      
    default:
      return state;
  }
}

// Create context
const FormContext = createContext();

// Custom hook for easy context usage
export const useFormContext = () => useContext(FormContext);

// Helper to join the structured address fields into a single string
// e.g. "12 Smith Street, Brunswick VIC 3056"
export const buildFullAddress = (formData) => {
  const line1 = (formData.streetAddress || '').trim();
  const line2 = [formData.suburb, formData.state, formData.postcode]
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join(' ');
  return [line1, line2].filter(Boolean).join(', ');
};

// Provider component
export const FormProvider = ({ children, userData, userType }) => {
  const [state, dispatch] = useReducer(formReducer, initialFormState);
  
  // Initialize with user data if available
  useEffect(() => {
    if (userData) {
      dispatch({ 
        type: 'INITIALIZE_USER_DATA', 
        userData: {
          firstName: userData[4] || '',
          email: userData[2] || '',
          mobile: userData[3] || '',
          workStatus: userData[6] || ''
        }
      });
    }
  }, [userData]);
  

  

  // Helper functions for commonly used dispatch patterns
  const setFieldValue = (field, value) => {
    dispatch({ type: 'SET_FIELD_VALUE', field, value });
  };
  


  const setFile = (field, file) => {
    dispatch({ type: 'SET_FILE', field, file });
  };
  
  const setFormErrors = (errors) => {
    if (typeof errors === 'function') {
    dispatch({ 
      type: 'SET_FORM_ERRORS', 
      errors: errors(state.formErrors) 
    });
  } else {
    dispatch({ type: 'SET_FORM_ERRORS', errors });
  }
  };
  
  const setCurrentStep = (step) => {
    dispatch({ type: 'SET_CURRENT_STEP', step });
  };
  
  const markStepCompleted = (step) => {
    dispatch({ type: 'MARK_STEP_COMPLETED', step });
  };
  
  const setSubmitting = (isSubmitting) => {
    dispatch({ type: 'SET_SUBMITTING', isSubmitting });
  };
  
  const setSubmitMessage = (message) => {
    dispatch({ type: 'SET_SUBMIT_MESSAGE', message });
  };

  const setModifiedFields = (fields) => {
  if (typeof fields === 'function') {
    dispatch({
      type: 'SET_MODIFIED_FIELDS',
      fields: fields(state.modifiedFields)
    });
  } else {
    // If it's just a value
    dispatch({
      type: 'SET_MODIFIED_FIELDS',
      fields: fields
    });
  }
};
  
  const value = {
    ...state,
  userType,
  userData,
  setFieldValue,
  setFile,
  setFormErrors,
  setCurrentStep,
  markStepCompleted,
  setSubmitting,
  setSubmitMessage,
  setModifiedFields
  
  };
  
  return (
    <FormContext.Provider value={value}>
      {children}
    </FormContext.Provider>
  );
};

export default FormContext;